import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ValidationStats {
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
  inProgress: number;
}

interface ValidationResult {
  id: string;
  name: string;
  email: string;
  phone: string;
  status: 'valid' | 'invalid' | 'duplicate';
  issues: string[];
  timestamp: string;
}

interface ContactList {
  id: string;
  name: string;
  contactCount: number;
  lastUpdated: string;
}

const ContactValidation = () => {
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState<ValidationStats>({
    total: 0,
    valid: 0,
    invalid: 0,
    duplicates: 0,
    inProgress: 0
  });
  const [results, setResults] = useState<ValidationResult[]>([]);
  const [activeTab, setActiveTab] = useState<'all' | 'valid' | 'invalid' | 'duplicates'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [validationSettings, setValidationSettings] = useState({
    validateEmails: true,
    validatePhones: true,
    checkDuplicates: true,
    autoFix: false
  });
  
  // New state for contact list selection
  const [selectedList, setSelectedList] = useState<string>('');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [contactLists, setContactLists] = useState<ContactList[]>([
    { id: '1', name: 'All Contacts', contactCount: 25678, lastUpdated: '2023-10-15' },
    { id: '2', name: 'VIP Customers', contactCount: 1250, lastUpdated: '2023-10-10' },
    { id: '3', name: 'Newsletter Subscribers', contactCount: 8750, lastUpdated: '2023-10-05' },
    { id: '4', name: 'Event Attendees', contactCount: 3200, lastUpdated: '2023-09-28' }
  ]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Simulate validation process
  useEffect(() => {
    if (isValidating) {
      // Simulate loading contacts
      setStats(prev => ({
        ...prev,
        total: 25678,
        inProgress: 25678
      }));

      // Simulate validation process
      const interval = setInterval(() => {
        setProgress(prev => {
          const newProgress = prev + 1;
          
          // Update stats as validation progresses
          if (newProgress <= 100) {
            setStats(prevStats => {
              const processed = Math.floor((newProgress / 100) * 25678);
              const remaining = 25678 - processed;
              
              // Simulate validation results
              const valid = Math.floor(processed * 0.913);
              const invalid = Math.floor(processed * 0.048);
              const duplicates = Math.floor(processed * 0.039);
              
              return {
                total: 25678,
                valid,
                invalid,
                duplicates,
                inProgress: remaining
              };
            });
            
            // Add sample results
            if (newProgress % 5 === 0) {
              const newResult: ValidationResult = {
                id: `contact-${newProgress}`,
                name: `Contact ${newProgress}`,
                email: `contact${newProgress}@example.com`,
                phone: `+1 (555) ${String(newProgress).padStart(4, '0')}`,
                status: newProgress % 10 === 0 ? 'invalid' : (newProgress % 15 === 0 ? 'duplicate' : 'valid'),
                issues: newProgress % 10 === 0 ? ['Invalid email format'] : (newProgress % 15 === 0 ? ['Duplicate phone number'] : []),
                timestamp: new Date().toISOString()
              };
              
              setResults(prev => [newResult, ...prev]);
            }
          } else {
            // Validation complete
            clearInterval(interval);
            setIsValidating(false);
            setProgress(100);
          }
          
          return newProgress;
        });
      }, 100);
      
      return () => clearInterval(interval);
    }
  }, [isValidating]);

  // Start validation process
  const startValidation = () => {
    if (!selectedList && !uploadedFile) {
      alert('Please select a contact list or upload a file to validate');
      return;
    }
    
    setIsValidating(true);
    setProgress(0);
    setResults([]);
  };

  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setUploadedFile(e.target.files[0]);
      setSelectedList(''); // Clear selected list when file is uploaded
    }
  };

  // Handle list selection
  const handleListSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedList(e.target.value);
    setUploadedFile(null); // Clear uploaded file when list is selected
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset file input
    }
  };

  // Trigger file input click
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Filter results based on active tab and search term
  const filteredResults = results.filter(result => {
    // Filter by tab
    if (activeTab !== 'all' && result.status !== activeTab) {
      return false;
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        result.name.toLowerCase().includes(searchLower) ||
        result.email.toLowerCase().includes(searchLower) ||
        result.phone.includes(searchTerm)
      );
    }
    
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/superadmin/contact-management')}
            className="mr-4 text-gray-600 hover:text-gray-800"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-medium">Contact Validation</h1>
        </div>
        
        {!isValidating && (
          <button
            onClick={startValidation}
            className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 transition-colors flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Start Validation
          </button>
        )}
      </div>

      {/* Contact List Selection Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Select Contact List</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Existing Contact Lists */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3">Existing Contact Lists</h3>
            <div className="mb-4">
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                value={selectedList}
                onChange={handleListSelection}
                disabled={isValidating}
              >
                <option value="">Select a contact list</option>
                {contactLists.map(list => (
                  <option key={list.id} value={list.id}>
                    {list.name} ({list.contactCount.toLocaleString()} contacts)
                  </option>
                ))}
              </select>
            </div>
            
            {selectedList && (
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Last updated:</span> {contactLists.find(l => l.id === selectedList)?.lastUpdated}
                </p>
              </div>
            )}
          </div>
          
          {/* Upload New Contact List */}
          <div className="border rounded-lg p-4">
            <h3 className="font-medium mb-3">Upload New Contact List</h3>
            <div className="mb-4">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".csv,.xlsx,.xls,.txt"
                className="hidden"
                disabled={isValidating}
              />
              <button
                onClick={triggerFileInput}
                className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-md text-gray-600 hover:border-[#FF5722] hover:text-[#FF5722] transition-colors flex items-center justify-center"
                disabled={isValidating}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadedFile ? uploadedFile.name : 'Choose file to upload'}
              </button>
            </div>
            
            {uploadedFile && (
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">File type:</span> {uploadedFile.type || 'Unknown'}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Size:</span> {(uploadedFile.size / 1024).toFixed(2)} KB
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div className="mt-4 text-sm text-gray-600">
          <p>Select an existing contact list from your platform or upload a new one to validate.</p>
          <p className="mt-1">Supported formats: CSV, Excel, TXT</p>
        </div>
      </div>

      {/* Validation Progress */}
      {isValidating && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium">Validation Progress</h2>
            <span className="text-sm text-gray-600">{progress}% Complete</span>
          </div>
          
          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4">
            <div 
              className="bg-[#FF5722] h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Total Contacts</h3>
              <div className="text-2xl font-bold text-gray-800">{stats.total.toLocaleString()}</div>
              <p className="text-sm text-gray-600">To be validated</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Valid</h3>
              <div className="text-2xl font-bold text-green-600">{stats.valid.toLocaleString()}</div>
              <p className="text-sm text-gray-600">{((stats.valid / (stats.total - stats.inProgress)) * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Invalid</h3>
              <div className="text-2xl font-bold text-red-600">{stats.invalid.toLocaleString()}</div>
              <p className="text-sm text-gray-600">{((stats.invalid / (stats.total - stats.inProgress)) * 100).toFixed(1)}%</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium mb-2">Duplicates</h3>
              <div className="text-2xl font-bold text-yellow-600">{stats.duplicates.toLocaleString()}</div>
              <p className="text-sm text-gray-600">{((stats.duplicates / (stats.total - stats.inProgress)) * 100).toFixed(1)}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Validation Settings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-medium mb-4">Validation Settings</h2>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <p className="font-medium">Validate Email Addresses</p>
              <p className="text-sm text-gray-600">Check email format and domain validity</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={validationSettings.validateEmails}
                onChange={() => setValidationSettings(prev => ({
                  ...prev,
                  validateEmails: !prev.validateEmails
                }))}
                disabled={isValidating}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <p className="font-medium">Validate Phone Numbers</p>
              <p className="text-sm text-gray-600">Check phone number format and validity</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={validationSettings.validatePhones}
                onChange={() => setValidationSettings(prev => ({
                  ...prev,
                  validatePhones: !prev.validatePhones
                }))}
                disabled={isValidating}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <p className="font-medium">Check for Duplicates</p>
              <p className="text-sm text-gray-600">Identify duplicate contacts across groups</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={validationSettings.checkDuplicates}
                onChange={() => setValidationSettings(prev => ({
                  ...prev,
                  checkDuplicates: !prev.checkDuplicates
                }))}
                disabled={isValidating}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
            </label>
          </div>
          
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div>
              <p className="font-medium">Auto-Fix Issues</p>
              <p className="text-sm text-gray-600">Automatically correct common validation issues</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input 
                type="checkbox" 
                className="sr-only peer" 
                checked={validationSettings.autoFix}
                onChange={() => setValidationSettings(prev => ({
                  ...prev,
                  autoFix: !prev.autoFix
                }))}
                disabled={isValidating}
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#FF5722]/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#FF5722]"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Validation Results */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium">Validation Results</h2>
          
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Search contacts..."
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            
            <select
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value as 'all' | 'valid' | 'invalid' | 'duplicates')}
            >
              <option value="all">All Results</option>
              <option value="valid">Valid</option>
              <option value="invalid">Invalid</option>
              <option value="duplicates">Duplicates</option>
            </select>
          </div>
        </div>
        
        {results.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No validation results</h3>
            <p className="mt-1 text-sm text-gray-500">
              {isValidating 
                ? "Validation in progress. Results will appear here." 
                : "Start the validation process to see results."}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issues
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredResults.map((result) => (
                  <tr key={result.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{result.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{result.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{result.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        result.status === 'valid' 
                          ? 'bg-green-100 text-green-800' 
                          : result.status === 'invalid' 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {result.status.charAt(0).toUpperCase() + result.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">
                        {result.issues.length > 0 ? (
                          <ul className="list-disc list-inside">
                            {result.issues.map((issue, index) => (
                              <li key={index}>{issue}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-green-600">No issues</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(result.timestamp).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactValidation; 